import {
  anteilDesTages,
  feiertagName,
  fensterDesTages,
  fensterKuerzen,
  hatEigeneZeit,
  markierKlasse,
  markierLeiste,
  MARKIER_STUFE_LABELS,
  tagesabwesenheit,
  wirksamerMarkierTag,
} from '@netcon-work/shared'
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  pointerWithin,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type Announcements,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, Clock, GripVertical, Plus, Sun, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { AufgabenVerweis } from '../AufgabeKontext'
import { termineVorschauQuery } from '../../lib/queries'
import { ArbeitszeitBlatt } from './ArbeitszeitBlatt'
import { EinplanenDialog, type EinplanenGruppe } from './EinplanenDialog'
import { cn } from '../../lib/cn'
import { belegteStunden, dauerStunden, gefaehrdet, tagBelegen, type Block } from './tagesplan'
import { weltdauern } from '../../lib/dauer'
import { kalenderTagAbholen, useMarkieren } from '../../lib/markierung'
import { useTelefonLayout } from '../../lib/medien'
import type { Naechste } from './lage'
import { tagesschluesselVon, useTagesplan } from './useTagesplan'
import {
  aufgabeAusZug,
  MarkiertPasstNicht,
  tagAusZiel,
  Wochenleiste,
  ZIEL_RASTER,
  zugBlock,
  ZiehGriff,
  ZumEinplanen,
  type Tagesspalte,
} from './Wochenleiste'

/**
 * **Der Kalender-Modus von „Mein Tag"**
 * ([Z-19](../../../../../docs/phasen/zusatz-19-tagesplan.md) AP 19.6).
 *
 * Der Tag als Zeitstrahl: echte Termine aus Outlook, die Grundlast für Mail und Telefon, und was
 * in Arbeit ist in den verbleibenden Lücken. Die Rechnung steht in
 * [`tagesplan.ts`](./tagesplan.ts); hier wird nur gezeichnet.
 *
 * **Nichts wird in den Kalender zurückgeschrieben** (Nils, 30.09.2026: *„dafür ändert sich ja zu
 * dynamisch und zu viel von den aufgaben her"*). Damit braucht Work kein Schreibrecht auf
 * fremde Kalender – das vorhandene Leserecht genügt.
 *
 * **Ohne Outlook-Verbindung** bleibt es ein leerer Tag mit Arbeitszeit und Grundlast, und darüber
 * steht ein Satz, der das sagt. Kein Fehler: Die Ansicht ist auch ohne Kalender brauchbar, nur
 * weniger genau.
 */

/**
 * **Die Grundhöhe einer Stunde** – das Maß, mit dem gerechnet wird, solange nichts gemessen ist.
 *
 * Seit dem 02.10.2026 wächst die Spur in die verfügbare Höhe (Nils: *„die Höhe, wenn vorhanden,
 * kann proportional besser genutzt werden"*). Diese Zahl bleibt die **Untergrenze**: Darunter
 * wird ein Viertelstundentermin unlesbar, und dann ist Scrollen besser als Quetschen.
 */
const STUNDE_PX = 56
const UHR = (h: number) =>
  `${String(Math.floor(h)).padStart(2, '0')}:${String(Math.round((h % 1) * 60)).padStart(2, '0')}`

/** Einen Tagesschlüssel um `n` Tage verschieben – über `Date`, damit Monatsenden stimmen. */
function tagVerschieben(tag: string, n: number): string {
  const d = new Date(`${tag}T12:00:00`)
  d.setDate(d.getDate() + n)
  return tagesschluesselVon(d)
}

/** „Heute", „Morgen", sonst „Do, 2. Okt." – die kurze Form, der Kopf ist schmal. */
function tagName(tag: string, heute: string): string {
  if (tag === heute) return 'Heute'
  if (tag === tagVerschieben(heute, 1)) return 'Morgen'
  if (tag === tagVerschieben(heute, -1)) return 'Gestern'
  return new Date(`${tag}T12:00:00`).toLocaleDateString('de-DE', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })
}

/**
 * **Wer überlappt, teilt sich die Breite.**
 *
 * Erst werden die Klötze nach Beginn sortiert und in **Gruppen** zerlegt: Eine Gruppe endet,
 * sobald ein Klotz beginnt, nachdem alles Bisherige vorbei ist. Innerhalb einer Gruppe bekommt
 * jeder die erste Spalte, die zu seinem Beginn frei ist – dieselbe Rechnung, die jeder
 * Kalender macht.
 *
 * **Die Breite gilt für die ganze Gruppe**, nicht je Klotz: Stünden in einer Gruppe von drei
 * zwei nebeneinander und einer allein, sprängen die Ränder untereinander hin und her. Ein
 * gemeinsames Raster ist ruhiger zu lesen, auch wenn es ein Stück Breite verschenkt.
 */
/**
 * **Die kleinste Höhe, die ein Klotz einnimmt** – in Stunden.
 *
 * `Klotz` zwingt jeden Block auf mindestens 20 px, sonst wäre ein Viertelstundentermin 11 px
 * hoch und unleserlich. Diese 20 px entsprechen bei {@link STUNDE_PX} aber mehr Zeit, als der
 * Block dauert – und genau daran lag der Fehler, den Nils am 01.10.2026 im Bild sah: „Daily,
 * 09:15–09:30" wurde von der Grundlast ab 09:30 verdeckt. **Zeitlich überlappen sie nicht**,
 * aber gezeichnet schon.
 *
 * Deshalb rechnet die Spaltenaufteilung mit dem **gezeichneten** Ende, nicht mit dem echten.
 * Dann erkennt sie die Berührung und stellt beide nebeneinander, statt sie übereinanderzulegen.
 */
const MINDESTHOEHE_STUNDEN = 23 / STUNDE_PX

/** Das Ende, wie es gezeichnet wird – mindestens so lang, dass der Klotz lesbar bleibt. */
const gezeichnetesEnde = (b: Block): number => Math.max(b.bis, b.von + MINDESTHOEHE_STUNDEN)

function spaltenVerteilen(bloecke: readonly Block[]): { spalte: number; anzahl: number }[] {
  const ergebnis: { spalte: number; anzahl: number }[] = bloecke.map(() => ({
    spalte: 0,
    anzahl: 1,
  }))
  const nachZeit = bloecke
    .map((b, i) => ({ b, i }))
    .sort((x, y) => x.b.von - y.b.von || x.b.bis - y.b.bis)

  let gruppe: { b: Block; i: number }[] = []
  let gruppenEnde = -Infinity

  const gruppeAbschliessen = () => {
    if (gruppe.length === 0) return
    const enden: number[] = []
    for (const { b, i } of gruppe) {
      let spalte = enden.findIndex((ende) => ende <= b.von + 1e-9)
      if (spalte === -1) {
        spalte = enden.length
        enden.push(gezeichnetesEnde(b))
      } else {
        enden[spalte] = gezeichnetesEnde(b)
      }
      ergebnis[i] = { spalte, anzahl: 1 }
    }
    for (const { i } of gruppe) ergebnis[i]!.anzahl = enden.length
    gruppe = []
    gruppenEnde = -Infinity
  }

  for (const eintrag of nachZeit) {
    if (eintrag.b.von >= gruppenEnde - 1e-9) gruppeAbschliessen()
    gruppe.push(eintrag)
    gruppenEnde = Math.max(gruppenEnde, gezeichnetesEnde(eintrag.b))
  }
  gruppeAbschliessen()
  return ergebnis
}

/**
 * **Wie weit die Vorschau die echten Termine kennt** (AP 19.5). Vier Wochen reichen: Was danach
 * fällig wird, ist keine Warnung mehr, sondern eine Planung. Die Zahl steht hier, weil zwei
 * Stellen sie brauchen – die Abfrage und der Satz, der sie erklärt.
 */
const VORSCHAU_TAGE = 28

const stunden = (h: number) =>
  h === 0.5 ? '30 min' : `${h.toLocaleString('de-DE', { maximumFractionDigits: 1 })} h`

/** `2026-10-09` → `Freitag, 9. Oktober`. Mittags gelesen, damit keine Zeitzone den Tag kippt. */
const tagLesbar = (schluessel: string) =>
  new Date(`${schluessel}T12:00:00`).toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })

export function TagesplanAnsicht({
  vorlauf = [],
}: {
  /**
   * **Die Rangfolge des Vorlaufs** (Z-20) – für „Zum Einplanen". Von „Mein Tag" hereingereicht,
   * das sie ohnehin rechnet; ein zweites `useMeinTagLage` hier rechnete den ganzen Bestand
   * doppelt.
   */
  vorlauf?: readonly Naechste[]
} = {}) {
  const markieren = useMarkieren()
  const telefon = useTelefonLayout()
  /** Aus der Ausblickzeile im Vorlauf („Übermorgen 1") kommt man gleich beim richtigen Tag an. */
  const [tag, setTag] = useState(() => kalenderTagAbholen() ?? tagesschluesselVon(new Date()))
  const [zeitOffen, setZeitOffen] = useState(false)
  const [gefahrAlle, setGefahrAlle] = useState(false)
  /** Die Rechnung des Tages – dieselbe wie in „My Work" (Z-24 AP 24.2). */
  const {
    aufgaben,
    bestand,
    markierungen,
    uhrzeiten,
    termine,
    kalender,
    zeitFelder,
    welten,
    heute,
    zeit,
    meineAbwesenheiten,
    datum,
    feiertag,
    abwesend,
    fenster,
    arbeitsfrei,
    plan,
  } = useTagesplan(tag)

  /**
   * **Was rechnerisch zu spät kommt** (AP 19.8) – über den ganzen Bestand, nicht über den
   * angezeigten Tag. Die Frage *„schaffe ich das noch"* hängt nicht daran, welchen Tag man
   * gerade ansieht; sie bleibt beim Blättern dieselbe.
   */
  /**
   * **Die echten Termine der nächsten Wochen** (AP 19.5) – ein Aufruf, nach Tagen gebündelt.
   *
   * Die Spanne beginnt heute, nicht am angezeigten Tag: Die Frage *„schaffe ich das noch"*
   * hängt nicht daran, welchen Tag man gerade ansieht. Vier Wochen reichen – was danach fällig
   * wird, ist keine Warnung mehr, sondern eine Planung.
   */
  const vorschauBis = useMemo(
    () => tagesschluesselVon(new Date(Date.now() + VORSCHAU_TAGE * 86_400_000)),
    [heute],
  )
  const { data: vorschau } = useQuery(termineVorschauQuery(heute, vorschauBis))

  const gefahren = useMemo(
    () =>
      gefaehrdet(
        aufgaben
          .filter(
            (a) =>
              a.status !== 'erledigt' && a.status !== 'review' && a.status !== 'zurueckgestellt',
          )
          .map((a) => ({
            id: a.id,
            title: a.title,
            groesse: a.groesse ?? null,
            priority: a.priority ?? null,
            dueAt: a.dueAt ?? null,
            updatedAt: a.updatedAt,
            workspaceId: a.workspaceId ?? null,
            termin: termine.get(a.id) ?? null,
            sourceCreatedAt: a.sourceCreatedAt ?? null,
            createdAt: a.createdAt,
          })),
        new Date(),
        zeit,
        weltdauern(welten),
        (d) => anteilDesTages(tagesabwesenheit(meineAbwesenheiten, tagesschluesselVon(d))),
        (d) => belegteStunden(vorschau?.tage?.[tagesschluesselVon(d)] ?? [], zeit),
        /** Z-20: was für heute markiert ist (auch liegengeblieben), kommt zuerst dran. */
        new Set(
          [...markierungen]
            .filter(([, am]) => wirksamerMarkierTag(am, heute) === heute)
            .map(([id]) => id),
        ),
      ),
    [aufgaben, zeit, welten, meineAbwesenheiten, vorschau, termine, markierungen, heute],
  )

  /**
   * **Die Wochenleiste** (Z-20): heute und die nächsten vier Werktage, je Spalte das Markierte
   * und die freie Zeit. Frei heißt hier dasselbe wie im Kopf des Rasters – Fenster des Tages,
   * Abwesenheit, Termine aus der Vorschau, Grundlast –, gerechnet mit **demselben** Planer und
   * leerer Aufgabenliste. Eine zweite Formel für „frei" liefe beim ersten Feiertag auseinander.
   */
  const spaltenDerWoche: Tagesspalte[] = useMemo(() => {
    const dauern = weltdauern(welten)
    const mitEigenerZeit = new Set(welten.filter(hatEigeneZeit).map((w) => w.id))
    return markierLeiste(heute).map((t) => {
      const d = new Date(`${t}T12:00:00`)
      const ab = tagesabwesenheit(meineAbwesenheiten, t)
      const f = ab
        ? fensterKuerzen(fensterDesTages(zeitFelder, welten, d), ab.verbleibt)
        : fensterDesTages(zeitFelder, welten, d)
      const termine = t === tag ? (kalender?.termine ?? []) : (vorschau?.tage?.[t] ?? [])
      const frei = tagBelegen([], termine, {
        fenster: f,
        grundlast: zeit.grundlast,
        mitEigenerZeit,
        ...(t === heute ? { abStunde: new Date().getHours() + new Date().getMinutes() / 60 } : {}),
      }).freiNetto
      const markiertHier = bestand
        .filter((a) => {
          const am = markierungen.get(a.id)
          return am !== undefined && wirksamerMarkierTag(am, heute) === t
        })
        .map((a) => ({
          id: a.id,
          title: a.title,
          workspaceId: a.workspaceId,
          am: markierungen.get(a.id)!,
          uhr: uhrzeiten.get(a.id)?.am === t ? UHR(uhrzeiten.get(a.id)!.um / 60) : undefined,
          dauer: dauerStunden(
            {
              id: a.id,
              title: a.title,
              groesse: a.groesse ?? null,
              workspaceId: a.workspaceId ?? null,
            },
            dauern,
          ),
        }))
      return {
        tag: t,
        /**
         * **„Morgen" wie an den Knöpfen** – in Werktagen. Freitags ist die zweite Spalte Montag
         * und heißt „Morgen"; der Kalendertag hieße sonst Samstag. Weiter weg der Wochentag
         * allein: das Datum steht ohnehin rechts daneben (05.10.2026, im Browser stand es doppelt).
         */
        name: spaltenName(t, heute),
        aufgaben: markiertHier,
        frei,
        frei_grund:
          f.length === 0
            ? ab?.verbleibt === 'nichts'
              ? 'du bist nicht da'
              : (feiertagName(d) ?? 'kein Arbeitstag')
            : null,
      }
    })
  }, [
    heute,
    tag,
    welten,
    zeitFelder,
    meineAbwesenheiten,
    kalender,
    vorschau,
    zeit.grundlast,
    bestand,
    markierungen,
    uhrzeiten,
  ])

  /** Was nicht markiert ist, in der Reihenfolge des Vorlaufs. */
  const zumEinplanen = useMemo(
    () => vorlauf.filter((z) => !markierungen.has(z.id)),
    [vorlauf, markierungen],
  )

  const sensoren = useSensors(
    /** Erst ab sechs Pixeln Weg ist es ein Zug – sonst fängt der Griff jeden Klick ab. */
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  )

  /**
   * **Das Raster reicht so weit wie der Tag, nicht so weit wie die Arbeitszeit.**
   *
   * Ein Termin von 17–20 Uhr lag bisher unterhalb des Kastens und schob sich über die Karte
   * darunter – sichtbar am 30.09.2026 beim „Ak Symposium". Der Grund: Das Raster war fest
   * `bis − von` hoch, die Klötze wurden aber absolut positioniert und ragten einfach hinaus.
   *
   * Statt sie abzuschneiden wächst das Raster mit. Einen Termin zu beschneiden hieße, aus
   * „drei Stunden Symposium" optisch eine Stunde zu machen – und der Abend ist genau die Zeit,
   * in der man wissen will, was noch kommt.
   */
  /**
   * **Was heute fertig wurde – als Zeitpunkt, nicht als Block**
   * (Nils, 01.10.2026: *„am besten wäre sonst auch einfach die Erledigten da in durchgestrichen
   * drin zu lassen … dann an dem Zeitpunkt (erledigt oder Review) da in den Kalender
   * eintragen"*).
   *
   * ## Warum nicht als Klotz mit Dauer
   *
   * Die erste Fassung zeichnete jede erledigte Aufgabe mit ihrer **geschätzten Dauer**, endend
   * am Fertig-Zeitpunkt. Im Bild war das unbrauchbar: Weil vieles fast gleichzeitig fertig
   * wird – dreimal 10:16, dreimal 13:53, dreimal 17:44 –, teilte die Spaltenrechnung die Zeile
   * in fünf Spalten, in die kein Titel mehr passte. Übrig blieben einzelne Buchstaben, und die
   * **echten Termine wurden mitgequetscht**.
   *
   * Der Denkfehler steckte eine Ebene tiefer: **Work weiß nicht, wie lange jemand daran gesessen
   * hat** – nur, wann es fertig wurde. Ein Klotz mit Dauer behauptet das Erste; ein Strich am
   * Zeitpunkt sagt nur das Zweite.
   *
   * ## Zusammengefasst je Viertelstunde
   *
   * Fünf Striche übereinander sind so unlesbar wie fünf Spalten. Was in dieselbe Viertelstunde
   * fällt, wird deshalb zu **einer** Marke – „3 erledigt", die Namen im `title`. Die Marken
   * liegen rechts in einer eigenen schmalen Spur, damit der Plan seine Breite behält.
   */
  const erledigt = useMemo(() => {
    const amTag = (w: string | Date | null | undefined) =>
      w ? tagesschluesselVon(new Date(w)) === tag : false
    const fertige = aufgaben
      .filter((a) => (a.status === 'erledigt' || a.status === 'review') && amTag(a.updatedAt))
      .map((a) => {
        const d = new Date(a.updatedAt!)
        return { id: a.id, titel: a.title, zeit: d.getHours() + d.getMinutes() / 60 }
      })
      .sort((a, b) => a.zeit - b.zeit)

    /**
     * **Je halbe Stunde eine Marke.** Erst waren es Viertelstunden – im Bild stößen die Marken
     * dann aneinander: 15 Minuten sind 14 px, eine lesbare Marke ist 17 px hoch. Eine halbe
     * Stunde gibt 28 px, und *„gegen halb elf fertig geworden"* ist genau genug.
     */
    const gruppen = new Map<number, { titel: string[]; zeit: number }>()
    for (const f of fertige) {
      const topf = Math.floor(f.zeit * 2) / 2
      const vorhanden = gruppen.get(topf)
      if (vorhanden) vorhanden.titel.push(f.titel)
      else gruppen.set(topf, { titel: [f.titel], zeit: topf })
    }
    return [...gruppen.values()]
  }, [aufgaben, tag])

  /**
   * **Auch die Erledigt-Marken bestimmen die Rasterhöhe** (Nils, 01.10.2026, auf dem echten
   * Telefon gesehen).
   *
   * Eine Marke um 21:30 lag unterhalb des Rasters, das nur bis 20:00 reichte – und wurde
   * deshalb **in die Karte darunter** gezeichnet. Die Spur ist absolut im Raster gesetzt; was
   * sie überragt, landet nicht am Rand, sondern irgendwo.
   *
   * Im Gerätemodus war das nicht zu sehen, weil dort zu dem Zeitpunkt noch nichts so spät
   * fertig geworden war. Es brauchte den echten Tag.
   */
  /**
   * **Die Spur nimmt die Höhe, die da ist** (Nils, 02.10.2026).
   *
   * An einem Tag mit wenigen Terminen stand unter dem Raster die halbe Seite leer, während die
   * Stunden auf 56 px gedrängt waren. Gemessen wird – wie beim Rad – am gerenderten Kasten, weil
   * über dem Raster die Termine des Tages und die Abwesenheitszeile stehen und deren Höhe
   * täglich wechselt.
   *
   * **Nach unten gedeckelt, nicht nach oben:** Unter {@link STUNDE_PX} wird ein
   * Viertelstundentermin unlesbar; darüber schadet Luft nicht.
   */
  /**
   * **Die aktuelle Uhrzeit, jede Minute neu.** Ein Strich, der beim Öffnen stimmt und dann
   * stehenbleibt, ist schlimmer als keiner – wer ihn am Nachmittag noch beim Mittagessen sieht,
   * glaubt dem Plan nicht mehr.
   */
  const [jetztStunde, setJetztStunde] = useState(
    () => new Date().getHours() + new Date().getMinutes() / 60,
  )
  useEffect(() => {
    const takt = window.setInterval(
      () => setJetztStunde(new Date().getHours() + new Date().getMinutes() / 60),
      60_000,
    )
    return () => window.clearInterval(takt)
  }, [])

  const raster = useRef<HTMLDivElement>(null)
  const [stundePx, setStundePx] = useState(STUNDE_PX)

  const rand = [
    ...plan.bloecke,
    ...erledigt.map((g) => ({ von: g.zeit, bis: g.zeit + 0.5 })),
  ].reduce(
    (r, b) => ({ von: Math.min(r.von, b.von), bis: Math.max(r.bis, b.bis) }),
    fenster.reduce((r, f) => ({ von: Math.min(r.von, f.von), bis: Math.max(r.bis, f.bis) }), {
      von: zeit.von,
      bis: zeit.bis,
    }),
  )
  const anzeigeVon = Math.floor(rand.von)
  const reihen = Math.max(1, Math.ceil(rand.bis) - anzeigeVon)
  useEffect(() => {
    const messen = () => {
      const el = raster.current
      if (!el) return
      const platz = window.innerHeight - el.getBoundingClientRect().top - 24
      setStundePx(Math.max(STUNDE_PX, Math.floor(platz / Math.max(1, reihen))))
    }
    messen()
    window.addEventListener('resize', messen)
    return () => window.removeEventListener('resize', messen)
  }, [reihen])

  const spalten = spaltenVerteilen(plan.bloecke)

  /**
   * **Loslassen über einem Tag markiert für diesen Tag – über dem Raster auch für eine Uhrzeit**
   * (Nils, 13.10.2026: *„ich möchte hier auch aufgaben auf bestimmte uhrzeiten legen können"*).
   *
   * Bis dahin zählte die Uhrzeit nicht, der Plan legte sofort neu (Z-19). Jetzt bleibt eine
   * Aufgabe dort stehen, wo man sie loslässt – auf die Viertelstunde, auch über einem Termin.
   * Ein Chip auf seine eigene Tagesspalte gezogen gibt die Uhrzeit wieder an den Plan zurück.
   */
  const spur = useRef<HTMLDivElement>(null)
  const [zielUhr, setZielUhr] = useState<number | null>(null)
  /** Was von außen gezogen wird – das Abbild unter dem Zeiger (`DragOverlay`). */
  const [gegriffen, setGegriffen] = useState<string | null>(null)
  /** Der Dialog „Einplanen" ist offen, mit dieser vorbelegten Uhrzeit (14.10.2026). */
  const [einplanenAb, setEinplanenAb] = useState<number | null>(null)

  /** Ein Klick auf eine leere Stelle im Raster: die Viertelstunde darunter. */
  const uhrAusKlick = (clientY: number): number => {
    const top = spur.current?.getBoundingClientRect().top ?? 0
    const viertel = Math.floor((anzeigeVon + (clientY - top) / stundePx) * 4) / 4
    return Math.min(Math.max(viertel, anzeigeVon), 23.75)
  }

  /** Vorbelegung für „+ Einplanen" im Kopf: heute ab jetzt, sonst ab Arbeitsbeginn. */
  const naechsteViertelstunde = () =>
    tag === heute ? Math.min(23.75, Math.ceil(jetztStunde * 4) / 4) : (fenster[0]?.von ?? zeit.von)

  /**
   * **Woraus man im Dialog wählt** – die Boxen rechts, in ihrer Reihenfolge. Was schon eine
   * Uhrzeit hat, fehlt: es steht ja schon im Raster.
   */
  const einplanenGruppen = useMemo((): EinplanenGruppe[] => {
    const festHier = (id: string) => uhrzeiten.get(id)?.am === tag
    const markiertHier = [
      ...plan.markiertPasstNicht,
      ...plan.bloecke.filter((b) => b.art === 'markiert').flatMap((b) => b.aufgabe ?? []),
    ]
    return [
      { titel: 'Für diesen Tag markiert', zeilen: markiertHier },
      { titel: 'Gefährdet', zeilen: gefahren.map((g) => g.aufgabe) },
      { titel: 'Zum Einplanen', zeilen: zumEinplanen.slice(0, 20) },
    ].map((g) => ({
      titel: g.titel,
      zeilen: g.zeilen.filter((z) => !festHier(z.id)).map((z) => ({ id: z.id, title: z.title })),
    }))
  }, [plan, gefahren, zumEinplanen, uhrzeiten, tag])
  const einplanenBestand = useMemo(
    () =>
      bestand
        .filter((a) => a.status !== 'erledigt' && a.status !== 'zurueckgestellt')
        .map((a) => ({ id: a.id, title: a.title })),
    [bestand],
  )

  /**
   * **Welche Uhrzeit ein Zug gerade meint** – `null`, wenn er keine meint.
   *
   * Ein Klotz aus dem Raster wandert um den Weg, den er gezogen wurde: wer ihn in der Mitte
   * greift, will nicht, dass sein Anfang zum Mauszeiger springt. Eine Zeile von außen dagegen
   * beginnt dort, wo der Zeiger ist. Mit der Tastatur gibt es keinen Zeiger – dann zählt nur der Tag.
   */
  const uhrAusZug = (e: DragMoveEvent | DragEndEvent): number | null => {
    const el = spur.current
    if (e.over?.id !== ZIEL_RASTER || !el) return null
    const kasten = el.getBoundingClientRect()
    const von = (e.active.data.current as { von?: number } | undefined)?.von
    let stunde: number
    if (von !== undefined) {
      stunde = von + e.delta.y / stundePx
    } else {
      const start = e.activatorEvent
      if (!start || !('clientY' in start)) return null
      const y = (start as MouseEvent).clientY + e.delta.y - kasten.top
      if (y < 0 || y > kasten.height) return null
      stunde = anzeigeVon + y / stundePx
    }
    const viertel = Math.round(stunde * 4) / 4
    return Math.min(Math.max(viertel, anzeigeVon), anzeigeVon + reihen - 0.25, 23.75)
  }

  const gezogen = (e: DragMoveEvent) => {
    const uhr = uhrAusZug(e)
    setZielUhr((alt) => (alt === uhr ? alt : uhr))
  }

  const gegriffenStart = (e: DragStartEvent) => {
    const daten = e.active.data.current as { titel?: string; von?: number } | undefined
    setGegriffen(daten?.von === undefined ? (daten?.titel ?? null) : null)
  }

  const losgelassen = (e: DragEndEvent) => {
    zugEndeteUm = Date.now()
    setZielUhr(null)
    setGegriffen(null)
    const taskId = aufgabeAusZug(e.active.id)
    const ziel = tagAusZiel(e.over?.id, tag)
    if (!taskId || !ziel) return
    const uhr = ziel === tag ? uhrAusZug(e) : null
    const fest = uhrzeiten.get(taskId)
    if (uhr !== null) {
      const um = Math.round(uhr * 60)
      if (fest?.am === ziel && fest.um === um) return
      markieren.mutate({ taskIds: [taskId], am: ziel, um })
      return
    }
    const loest = fest?.am === ziel && e.over?.id !== ZIEL_RASTER
    if (markierungen.get(taskId) === ziel && !loest) return
    markieren.mutate({ taskIds: [taskId], am: ziel })
  }

  return (
    <DndContext
      sensors={sensoren}
      onDragStart={gegriffenStart}
      onDragMove={gezogen}
      onDragEnd={losgelassen}
      onDragCancel={() => {
        setZielUhr(null)
        setGegriffen(null)
      }}
      collisionDetection={ablageNachZeiger}
      accessibility={{ screenReaderInstructions: ZIEH_ANLEITUNG, announcements: ZIEH_ANSAGEN }}
    >
      {/**
       * **`min-w-0` an jeder Ebene** (06.10.2026, auf dem Telefon gesehen): ohne sie drückten die
       * fünf Tagesspalten den Container auf 1.362 px, und die ganze Seite lief seitlich weg. Die
       * Wochenleiste scrollt jetzt in sich, nicht die Seite.
       */}
      <div className="flex min-w-0 flex-col gap-[18px]">
        <Wochenleiste
          spalten={spaltenDerWoche}
          angezeigt={tag}
          anzeigen={setTag}
          heute={heute}
          telefon={telefon}
          /**
           * „+ Einplanen für Mittwoch" auf der Kachel (15.10.2026): den Tag zeigen und den Dialog
           * öffnen – heute ab der nächsten Viertelstunde, sonst ab Arbeitsbeginn.
           */
          einplanen={(t) => {
            setTag(t)
            setEinplanenAb(t === heute ? Math.min(23.75, Math.ceil(jetztStunde * 4) / 4) : zeit.von)
          }}
        />
        {/**
         * **Drei Bereiche, zwei Anordnungen** (06.10.2026). Am Rechner links das Raster, rechts
         * oben die Warnungen, darunter „Zum Einplanen". Auf dem Telefon **Warnungen → Raster →
         * Zum Einplanen** – Z-19 hatte das versprochen (*„Auf dem Telefon steht die Warnung
         * oben"*), die einspaltige Liste setzte aber das Raster an den Anfang.
         */}
        <div className="grid min-w-0 grid-cols-1 items-start gap-[18px] [grid-template-areas:'oben'_'raster'_'unten'] lg:grid-cols-[minmax(380px,1.35fr)_minmax(280px,1fr)] lg:[grid-template-areas:'raster_oben'_'raster_unten'] lg:grid-rows-[auto_1fr]">
          <RasterAblage>
            <header className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5 text-[12.5px]">
              <button
                type="button"
                aria-label="Ein Tag zurück"
                onClick={() => setTag((t) => tagVerschieben(t, -1))}
                className="cursor-pointer rounded-md p-0.5 text-ink2 hover:bg-[#f3f2ef]"
              >
                <ChevronLeft className="size-[17px]" />
              </button>
              <b className="text-[13.5px]">{tagName(tag, heute)}</b>
              <button
                type="button"
                aria-label="Ein Tag vor"
                onClick={() => setTag((t) => tagVerschieben(t, 1))}
                className="cursor-pointer rounded-md p-0.5 text-ink2 hover:bg-[#f3f2ef]"
              >
                <ChevronRight className="size-[17px]" />
              </button>
              {tag !== heute && (
                <button
                  type="button"
                  onClick={() => setTag(heute)}
                  className="cursor-pointer rounded-full border border-line2 px-2 py-px text-[11.5px] text-ink2 hover:bg-[#f3f2ef]"
                >
                  Heute
                </button>
              )}
              {/* Die Zeit ist hier zu ändern, wo man sie vermisst – nicht drei Klicks entfernt. */}
              <button
                type="button"
                onClick={() => setZeitOffen(true)}
                className="cursor-pointer rounded-md px-1 text-muted underline decoration-dotted underline-offset-2 hover:text-accent-deep"
                title="Arbeitszeit, Grundlast und Arbeitstage einstellen"
              >
                {fenster.length === 0
                  ? `${UHR(zeit.von)}–${UHR(zeit.bis)}`
                  : fenster.map((f) => `${UHR(f.von)}–${UHR(f.bis)}`).join(' · ')}
              </button>
              <button
                type="button"
                onClick={() => setEinplanenAb(naechsteViertelstunde())}
                className="flex cursor-pointer items-center gap-1 rounded-full border border-sonne-line bg-sonne-bg px-2 py-px text-[11.5px] font-medium text-sonne hover:border-sonne"
                title="Eine Aufgabe auf eine Uhrzeit legen – auch ohne Ziehen"
              >
                <Plus className="size-3" aria-hidden /> Einplanen
              </button>
              <span className="ml-auto text-ink2">
                Frei: <b className="tabular-nums">{stunden(plan.freiRoh)}</b> −{' '}
                {stunden(zeit.grundlast)} Grundlast ={' '}
                <b className="tabular-nums">{stunden(plan.freiNetto)}</b>
              </span>
            </header>

            {/**
             * **Warum dieser Tag anders aussieht** – ein Satz, drei mögliche Gründe.
             *
             * Die Abwesenheit steht **vor** dem Feiertag, weil sie die persönlichere Auskunft ist:
             * Wer am Tag der Deutschen Einheit Urlaub hat, interessiert sich für den Urlaub. Beides
             * nebeneinander zu schreiben wäre zwei Zeilen für denselben leeren Tag.
             */}
            {abwesend ? (
              <p className="border-b border-line bg-[#f3f2ef] px-4 py-2 text-[12px] text-ink2">
                <b className="font-medium">
                  {abwesend.verbleibt === 'nichts'
                    ? 'Du bist nicht da'
                    : `Du bist nur ${abwesend.verbleibt === 'vormittag' ? 'vormittags' : 'nachmittags'} da`}
                </b>
                {abwesend.bis > tag && ` – noch bis ${tagLesbar(abwesend.bis)}`}
                {abwesend.verbleibt === 'nichts'
                  ? '. Deshalb ist nichts eingeplant; Termine stehen trotzdem.'
                  : '. Der Plan rechnet mit dem halben Tag.'}
                {!abwesend.verbindlich && ' (beantragt, noch nicht genehmigt)'}
              </p>
            ) : (
              arbeitsfrei && (
                <p className="border-b border-line bg-[#f3f2ef] px-4 py-2 text-[12px] text-ink2">
                  <b className="font-medium">
                    {feiertag ?? datum.toLocaleDateString('de-DE', { weekday: 'long' })}
                  </b>{' '}
                  – kein Arbeitstag, deshalb ist nichts eingeplant. Termine stehen trotzdem.
                </p>
              )
            )}
            {kalender?.verbunden === false && (
              <p className="border-b border-line bg-[#fdf3dc] px-4 py-2 text-[12px] text-[#8a6100]">
                Ohne Outlook-Verbindung rechnet der Plan mit einem freien Tag. Verbinden unter
                „Einstellungen".
              </p>
            )}
            {kalender?.fehler && (
              <p className="border-b border-line bg-bad-bg px-4 py-2 text-[12px] text-bad">
                {kalender.fehler}
              </p>
            )}

            {/**
             * **Wenn Zeit frei ist und trotzdem nichts darin steht, muss dastehen warum**
             * (Nils, 01.10.2026: *„irgendwie füllt der noch nicht die Aufgaben korrekt da rein.
             * Sicher, dass alles klappt?"*).
             *
             * Am 01.10. hatten **32 von 35** offenen Aufgaben keine Größe und galten deshalb als
             * „mittel" = 2 Stunden; die freien Strecken waren je eine Stunde lang. Der Plan tat das
             * Richtige und sah aus wie kaputt. Ein Plan, der schweigt, wird für kaputt gehalten.
             */}
            {plan.nichtPlatziert > 0 && plan.laengsteLuecke > 0 && (
              <p className="border-b border-line bg-[#f3f2ef] px-4 py-2 text-[12px] text-ink2">
                <b className="font-medium">
                  {plan.nichtPlatziert}{' '}
                  {plan.nichtPlatziert === 1 ? 'Aufgabe passt' : 'Aufgaben passen'} in keine Lücke
                </b>{' '}
                – die längste freie Strecke ist {stunden(plan.laengsteLuecke)}. Ohne Größe rechnet
                Work mit <b className="font-medium">2 h</b>; wer einer Aufgabe „klein" gibt, bekommt
                sie hier untergebracht.
              </p>
            )}

            <div ref={raster} className="relative py-2.5 pl-[58px] pr-4">
              {Array.from({ length: reihen }, (_, i) => (
                <div
                  key={i}
                  className="relative border-t border-dashed border-line"
                  style={{ height: stundePx }}
                >
                  <span className="absolute -left-[46px] -top-[9px] w-[38px] text-right text-[11px] tabular-nums text-muted">
                    {UHR(anzeigeVon + i)}
                  </span>
                </div>
              ))}
              {/**
               * **Die Spur der fertigen Sachen** – rechts, schmal, außerhalb der Spaltenrechnung.
               *
               * Sie darf den Plan nicht verengen: Was links steht, ist der Tag, der noch kommt; was
               * rechts steht, ist der, der schon war. Zwei Aussagen, zwei Spuren.
               */}
              {/**
               * **Die Linie „jetzt"** (Nils, 02.10.2026) – **nur am heutigen Tag**.
               *
               * An einem anderen Tag wäre sie eine Behauptung über eine Uhr, die dort nicht
               * gilt: Am Montag um 14 Uhr eine Linie zu zeigen, während man am Freitag draufsieht,
               * verwirrt mehr, als sie nützt.
               *
               * Sie liegt **über** den Klötzen, aber ohne Mausereignisse – sonst fänge ein Strich
               * von zwei Pixeln Klicks ab, die der Aufgabe darunter gehören.
               */}
              {tag === heute && jetztStunde > anzeigeVon && jetztStunde < anzeigeVon + reihen && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute right-4 left-[52px] z-10 border-t-2 border-warn"
                  style={{ top: 10 + (jetztStunde - anzeigeVon) * stundePx }}
                >
                  <span className="absolute -top-[5px] -left-[6px] block size-2.5 rounded-full bg-warn" />
                </div>
              )}

              {/**
               * **Auf dem Telefon ohne Erledigt-Spur** (06.10.2026): sie nahm 30 % der Breite,
               * und die Blöcke daneben zeigten nur noch „DATE" oder eine Uhrzeit ohne Titel. Das
               * Erledigte steht dort im Reiter „Erledigt".
               */}
              <div className="pointer-events-none absolute inset-y-2.5 right-4 w-[30%] max-sm:hidden">
                {erledigt.map((g) => (
                  <div
                    key={g.zeit}
                    className="pointer-events-auto absolute right-0 left-0 flex items-center gap-1.5 overflow-hidden rounded-md border border-[#e4e2dd] bg-[#f6f6f4] px-1.5 text-[11px] text-muted"
                    style={{ top: (g.zeit - anzeigeVon) * stundePx, height: 17 }}
                    title={`${UHR(g.zeit)} fertig geworden:\n${g.titel.join('\n')}`}
                  >
                    <span className="tabular shrink-0 opacity-70">{UHR(g.zeit)}</span>
                    <span className="truncate line-through">
                      {g.titel.length === 1 ? g.titel[0] : `${g.titel.length} erledigt`}
                    </span>
                  </div>
                ))}
              </div>

              {/**
               * **Der Plan macht dem Fertigen Platz – aber nur, wenn es etwas gibt.**
               *
               * Zuerst lagen beide übereinander, und die Marken verschwanden hinter den Terminen.
               * Jetzt endet die Planspur dort, wo die Erledigt-Spur beginnt. An einem Tag ohne
               * Erledigtes behält der Plan die ganze Breite – ein leerer Streifen wäre Platz, der
               * für nichts reserviert ist.
               */}
              <div
                ref={spur}
                className="absolute inset-y-2.5 left-[58px] cursor-cell"
                style={{ right: erledigt.length > 0 && !telefon ? 'calc(30% + 24px)' : 16 }}
                title="Klicken, um hier eine Aufgabe einzuplanen"
                /**
                 * **Eine leere Stelle anklicken öffnet „Einplanen"** mit dieser Uhrzeit (14.10.2026).
                 * Nur die Fläche selbst – ein Klick auf einen Klotz öffnet weiter die Aufgabe.
                 */
                onClick={(e) => {
                  /** Nach einem Zug schickt der Browser den Klick an den gemeinsamen Vorfahren – hierher. */
                  if (e.target !== e.currentTarget || Date.now() - zugEndeteUm < 400) return
                  setEinplanenAb(uhrAusKlick(e.clientY))
                }}
              >
                {plan.bloecke.map((b, i) => (
                  <Klotz
                    key={`${b.art}${i}`}
                    nr={i}
                    block={b}
                    von={anzeigeVon}
                    spalte={spalten[i]?.spalte ?? 0}
                    anzahl={spalten[i]?.anzahl ?? 1}
                    stundePx={stundePx}
                    ziehbar={!telefon && b.aufgabe !== undefined}
                    uhrLoesen={
                      b.art === 'fest' && b.aufgabe
                        ? () => markieren.mutate({ taskIds: [b.aufgabe!.id], am: tag })
                        : undefined
                    }
                  />
                ))}
                {/** Wohin der Zug gerade fällt – die Viertelstunde, auf der die Aufgabe beginnen wird. */}
                {zielUhr !== null && (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute right-0 left-0 z-20 border-t-2 border-dashed border-sonne"
                    style={{ top: (zielUhr - anzeigeVon) * stundePx }}
                  >
                    <span className="absolute -top-[10px] left-1 rounded bg-sonne px-1 text-[10.5px] font-semibold tabular-nums text-white">
                      {UHR(zielUhr)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </RasterAblage>

          <div className="flex min-w-0 flex-col gap-[18px] [grid-area:oben]">
            {zeitOffen && <ArbeitszeitBlatt schliessen={() => setZeitOffen(false)} />}

            <MarkiertPasstNicht
              telefon={telefon}
              aufgaben={plan.markiertPasstNicht}
              dauer={(id) => {
                const a = plan.markiertPasstNicht.find((x) => x.id === id)
                return a ? dauerStunden(a, weltdauern(welten)) : 0
              }}
            />

            {gefahren.length > 0 && (
              /**
               * **Die Warnung steht über dem Rest** – sie ist die einzige Zeile hier, die eine
               * Handlung verlangt. „Passt heute nicht mehr" ist eine Feststellung über den Tag;
               * „Gefährdet" ist eine über die Zusage, die jemand gegeben hat.
               */
              <section className="rounded-card border border-[#f3cfcf] bg-surface shadow-task">
                <h2 className="flex items-center justify-between border-b border-[#f3cfcf] bg-bad-bg px-4 py-3 text-[13.5px] text-bad">
                  Gefährdet
                  <span className="text-[12px] font-normal">{gefahren.length} Stück</span>
                </h2>
                <ul>
                  {/** Auf dem Telefon drei – sie stehen dort über dem Raster und sollen es nicht verdrängen. */}
                  {gefahren.slice(0, gefahrAlle ? undefined : telefon ? 3 : 8).map((g) => (
                    <li
                      key={g.aufgabe.id}
                      className="border-b border-line pl-2 last:border-0 hover:bg-[#fafaf8]"
                    >
                      {/** Ziehbar wie „Zum Einplanen" (14.10.2026) – auch Gefährdetes gehört oft an eine Uhrzeit. */}
                      <ZiehGriff
                        taskId={g.aufgabe.id}
                        titel={g.aufgabe.title}
                        herkunft="gefahr"
                        aus={telefon}
                      >
                        <AufgabenVerweis
                          aufgabeId={g.aufgabe.id}
                          className="flex w-full items-baseline gap-2.5 py-2 pr-4 pl-1 text-left"
                        >
                          <span className="min-w-0 flex-1">
                            <b className="block text-[13px] font-medium">{g.aufgabe.title}</b>
                            <span className="text-[11.5px] text-muted">
                              fällig{' '}
                              {g.frist.toLocaleDateString('de-DE', {
                                day: 'numeric',
                                month: 'short',
                              })}{' '}
                              {/** „kommt erst am Di., 6. Okt." war heute – dann heißt es auch so. */}
                              {tagesschluesselVon(g.fertigAm) === heute ? (
                                '· kommt heute dran'
                              ) : (
                                <>
                                  · kommt erst am{' '}
                                  {g.fertigAm.toLocaleDateString('de-DE', {
                                    weekday: 'short',
                                    day: 'numeric',
                                    month: 'short',
                                  })}{' '}
                                  dran
                                </>
                              )}
                            </span>
                          </span>
                          <span className="tabular whitespace-nowrap rounded-full bg-bad-bg px-[7px] text-[10.5px] font-bold text-bad">
                            {g.tageZuSpaet} {g.tageZuSpaet === 1 ? 'Tag' : 'Tage'}
                          </span>
                        </AufgabenVerweis>
                      </ZiehGriff>
                    </li>
                  ))}
                </ul>
                {gefahren.length > (telefon ? 3 : 8) && (
                  <button
                    type="button"
                    onClick={() => setGefahrAlle((x) => !x)}
                    aria-expanded={gefahrAlle}
                    className="tap w-full cursor-pointer px-4 py-2 text-left text-[11.5px] text-muted hover:text-accent-deep"
                  >
                    {gefahrAlle
                      ? '▴ weniger anzeigen'
                      : `▾ ${gefahren.length - (telefon ? 3 : 8)} weitere anzeigen`}
                  </button>
                )}
                {/**
                 * **Der Satz muss sagen, was die Rechnung wirklich weiß** – nicht, was sie einmal
                 * nicht wusste. Hier stand bis zum 02.10.2026 „ohne die Termine künftiger Tage"; seit
                 * `termineImZeitraum` (AP 19.5) stimmt das für vier Wochen nicht mehr. Ein Hinweis,
                 * der die eigene Auskunft schlechter macht, als sie ist, ist genauso falsch wie einer,
                 * der sie beschönigt – und er steht an der Stelle, an der jemand nachliest, warum er
                 * der Zahl glauben soll.
                 */}
                <p className="border-t border-line px-4 py-2 text-[11.5px] text-muted max-sm:hidden">
                  Gerechnet mit {stunden(zeit.bis - zeit.von - zeit.grundlast)} je Arbeitstag,
                  abzüglich deiner Termine und Abwesenheiten der nächsten {VORSCHAU_TAGE} Tage. Was
                  danach kommt, zählt als gewöhnlicher Tag – dort ist die Rechnung eher zu
                  optimistisch.
                </p>
              </section>
            )}

            {/**
             * **Zum Einplanen direkt unter „Gefährdet"** (05.10.2026, im Browser gesehen): ganz
             * unten lag die Liste anderthalb Bildschirme unter der Wochenleiste, und jeder Zug
             * wurde zur Fahrt. Die Warnung bleibt oben (Z-19), „Passt heute nicht mehr" rückt
             * darunter – es ist eine Feststellung, keine Handlung.
             */}
          </div>

          <div className="flex min-w-0 flex-col gap-[18px] [grid-area:unten]">
            <ZumEinplanen zeilen={zumEinplanen} telefon={telefon} />

            <section className="rounded-card border border-line bg-surface shadow-task">
              <h2 className="flex items-center justify-between border-b border-line px-4 py-3 text-[13.5px]">
                Passt heute nicht mehr
                <span className="text-[12px] font-normal text-muted">{plan.rest.length} Stück</span>
              </h2>
              {plan.rest.length === 0 ? (
                <p className="px-4 py-5 text-center text-[12.5px] text-muted">
                  Alles untergebracht.
                </p>
              ) : (
                <ul>
                  {plan.rest.map((a) => (
                    <li key={a.id} className="border-b border-line last:border-0">
                      <AufgabenVerweis
                        aufgabeId={a.id}
                        className="flex w-full items-baseline gap-2.5 px-4 py-2 text-left hover:bg-[#fafaf8]"
                      >
                        <span className="min-w-0 flex-1">
                          <b className="block text-[13px] font-medium">{a.title}</b>
                          <span className="text-[11.5px] text-muted">
                            {stunden(dauerStunden(a))}
                          </span>
                        </span>
                        <span className="whitespace-nowrap rounded-full border border-line bg-[#fafaf8] px-[7px] text-[10.5px] text-ink2">
                          morgen
                        </span>
                      </AufgabenVerweis>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>
      </div>
      {/**
       * **Das Abbild unter dem Zeiger** (14.10.2026). Die Zeile selbst bleibt in ihrer Box; sonst
       * schnitt die scrollende Wochenleiste sie ab, und man sah nicht, was man zog.
       */}
      <DragOverlay dropAnimation={null}>
        {gegriffen && (
          <div className="flex max-w-[280px] cursor-grabbing items-center gap-1.5 rounded-[9px] border border-sonne bg-sonne-bg px-2.5 py-1 text-[12.5px] font-medium text-sonne shadow-lg">
            <GripVertical className="size-3.5 shrink-0" aria-hidden />
            <span className="truncate">{gegriffen}</span>
            {zielUhr !== null && (
              <span className="tabular shrink-0 font-semibold">{UHR(zielUhr)}</span>
            )}
          </div>
        )}
      </DragOverlay>
      {einplanenAb !== null && (
        <EinplanenDialog
          tagName={tagName(tag, heute)}
          start={einplanenAb}
          gruppen={einplanenGruppen}
          bestand={einplanenBestand}
          einplanen={(taskId, um) => markieren.mutate({ taskIds: [taskId], am: tag, um })}
          schliessen={() => setEinplanenAb(null)}
        />
      )}
    </DndContext>
  )
}

/**
 * **Wohin ein Zug fällt, entscheidet der Mauszeiger** (05.10.2026, im Browser gefunden).
 *
 * Die Vorgabe von dnd-kit vergleicht die **Fläche** des gezogenen Elements mit den Zielen. Gezogen
 * wird hier aber eine ganze Zeile von 500 px – über der Spalte „Morgen" losgelassen, überdeckte sie
 * „Mittwoch" stärker, und die Aufgabe landete dort. Mit der Tastatur gibt es keinen Zeiger; dann
 * gilt das Ziel, dessen Mitte am nächsten liegt.
 */
const ablageNachZeiger: CollisionDetection = (args) => {
  const unterZeiger = pointerWithin(args)
  return unterZeiger.length > 0 ? unterZeiger : closestCenter(args)
}

/** „Mittwoch" – Heute/Morgen/Übermorgen in Werktagen wie an den Knöpfen, sonst der Wochentag. */
function spaltenName(tag: string, heute: string): string {
  const klasse = markierKlasse(tag, heute)
  if (klasse !== 'spaeter') return MARKIER_STUFE_LABELS[klasse]
  return new Date(`${tag}T12:00:00`).toLocaleDateString('de-DE', { weekday: 'long' })
}

/** Die Bedienhilfe für Bildschirmleser – dnd-kit bringt sie nur auf Englisch mit. */
const ZIEH_ANLEITUNG = {
  draggable:
    'Zum Greifen die Leertaste drücken. Mit den Pfeiltasten auf einen Tag oder im Raster auf eine Uhrzeit bewegen, mit der Leertaste ablegen, mit Escape abbrechen.',
}
const tagLesbarAusZiel = (id: string | number | undefined) =>
  typeof id === 'string' && id.startsWith('tag:') ? tagLesbar(id.slice(4)) : 'den angezeigten Tag'
const ZIEH_ANSAGEN: Announcements = {
  onDragStart: () => 'Aufgabe gegriffen.',
  onDragOver: ({ over }) => (over ? `Über ${tagLesbarAusZiel(over.id)}.` : 'Über keinem Tag.'),
  onDragEnd: ({ over }) =>
    over ? `Für ${tagLesbarAusZiel(over.id)} markiert.` : 'Abgelegt, nichts geändert.',
  onDragCancel: () => 'Abgebrochen, nichts geändert.',
}

/**
 * **Wann der letzte Zug endete** – damit das Loslassen über einem Klotz ihn nicht gleich öffnet.
 * Der Klotz ist ein Verweis (E-319); der Browser schickt nach dem Loslassen einen Klick hinterher.
 */
let zugEndeteUm = 0

/**
 * **Das Raster als Ablage** (Z-20) – eine Zeile hierher zu ziehen markiert sie für den Tag, der
 * gerade zu sehen ist, und seit 13.10.2026 für die Uhrzeit, an der man loslässt. Die Karte trägt dabei einen gestrichelten Sonnenrand, damit man sieht, dass
 * hier losgelassen werden kann.
 */
function RasterAblage({ children }: { children: ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: ZIEL_RASTER })
  return (
    <section
      ref={setNodeRef}
      className={cn(
        'min-w-0 rounded-card border border-line bg-surface shadow-task transition-colors [grid-area:raster]',
        isOver && 'border-dashed border-sonne ring-2 ring-sonne-line',
      )}
    >
      {children}
    </section>
  )
}

/**
 * **Ein Klotz auf dem Zeitstrahl.**
 *
 * Unter ~45 Minuten stehen Titel und Zeit **nebeneinander**: Zwei Zeilen passen dort nicht, und
 * `overflow: hidden` fräße ausgerechnet den Titel weg – dann stünde nur eine Uhrzeit da. Am
 * Mockup am 30.09.2026 genau so passiert und dort behoben.
 */
function Klotz({
  nr,
  block,
  von,
  spalte,
  anzahl,
  stundePx,
  ziehbar,
  uhrLoesen,
}: {
  nr: number
  block: Block
  von: number
  spalte: number
  anzahl: number
  stundePx: number
  /** Aufgaben lassen sich im Raster auf eine andere Uhrzeit ziehen (13.10.2026); Termine nicht. */
  ziehbar: boolean
  /** Nur an fest eingeplanten: die Uhrzeit wieder dem Plan überlassen. */
  uhrLoesen?: (() => void) | undefined
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: block.aufgabe ? zugBlock(block.aufgabe.id, nr) : `klotz:${nr}`,
    data: { von: block.von },
    disabled: !ziehbar,
  })
  const hoehe = Math.max(20, (block.bis - block.von) * stundePx - 3)
  const kurz = block.bis - block.von < 0.8
  const zeit = `${UHR(block.von)}–${UHR(block.bis)}`

  const stil =
    block.art === 'termin'
      ? 'bg-[#edecea] border-[#ddd9d2] text-ink2'
      : block.art === 'grundlast'
        ? 'border-[#e0ddd6] text-ink2 bg-[repeating-linear-gradient(135deg,#f1efeb,#f1efeb_6px,#e9e6e0_6px,#e9e6e0_12px)]'
        : block.art === 'vorschlag'
          ? /* Vorschläge sind ein Angebot, keine Zusage – gestrichelt und blasser. */
            'bg-[#f7fbff] border-dashed border-[#c3dcf5] text-[#2a5f8f] cursor-pointer hover:border-[#2a78d6]'
          : block.art === 'markiert'
            ? /* Z-20: vorgenommen – volle Fläche in Sonnenfarbe, dicker linker Rand. */
              'bg-sonne-bg border-sonne-line border-l-4 text-sonne cursor-pointer hover:border-sonne'
            : block.art === 'fest'
              ? /* 13.10.2026: an eine Uhrzeit gelegt – wie markiert, mit festem Rand. */
                'bg-sonne-bg border-sonne border-l-4 text-sonne cursor-pointer'
              : 'bg-[#eaf8f2] border-[#bfe6d6] text-[#0b6f4c] cursor-pointer hover:border-[#1baf7a]'

  const inhalt = (
    <>
      <span
        className={cn(
          'overflow-hidden text-ellipsis whitespace-nowrap font-medium',
          kurz && 'min-w-0 flex-1 text-[12px]',
        )}
      >
        {block.art === 'termin' ? '📅 ' : ''}
        {block.art === 'markiert' && (
          <Sun size={11} className="mr-1 inline align-[-1px]" aria-hidden />
        )}
        {block.art === 'fest' && (
          <Clock size={11} className="mr-1 inline align-[-1px]" aria-label="fest eingeplant" />
        )}
        {block.titel}
        {block.teil && (
          <span className="ml-1 font-normal opacity-75">
            · Teil {block.teil.nr}/{block.teil.von}
          </span>
        )}
      </span>
      {/**
       * **Nebeneinander hat der Titel Vorrang** (06.10.2026, auf dem Telefon gesehen): in einer
       * schmalen Spalte blieb sonst nur „16:45–17:0" stehen. Die Uhrzeit steht dann im Tooltip,
       * die Lage im Raster sagt sie ohnehin.
       */}
      <span
        className={cn(
          'text-[11px] tabular-nums opacity-80',
          kurz && 'flex-none',
          anzahl > 1 && 'max-sm:hidden',
        )}
      >
        {zeit}
      </span>
    </>
  )

  const klassen = cn(
    'flex h-full overflow-hidden rounded-[9px] border px-2.5 text-[12.5px]',
    kurz ? 'flex-row items-baseline gap-2 py-0.5' : 'flex-col justify-center py-1',
    stil,
  )
  /**
   * **Nebeneinander statt übereinander.** Jeder Klotz war `left-0 right-0` – zwei Termine zur
   * selben Zeit deckten einander vollständig zu, und man sah nur den später gezeichneten
   * („Mittagessen" lag auf „VreWin Matthias Treffen", 30.09.2026). Jetzt teilen sich
   * überlappende Klötze die Breite; `spaltenVerteilen` sagt, wer in welche Spalte gehört.
   */
  const breite = 100 / anzahl
  const lage = {
    top: (block.von - von) * stundePx,
    height: hoehe,
    left: `${spalte * breite}%`,
    width: `calc(${breite}% - ${anzahl > 1 ? 3 : 0}px)`,
    ...(transform ? { transform: `translate3d(0, ${transform.y}px, 0)` } : {}),
  }

  /**
   * **Ein Verweis, kein Knopf** (E-319, E-368). Ein `<button>` hat kein `href` – Strg+Klick und
   * mittlere Maustaste könnten damit keinen neuen Tab öffnen. Der Wächter
   * `lib/aufgabenverweis.test.ts` hält das fest; er hat genau diesen Fehler hier gefangen.
   */
  /*
   * **Auch ein Vorschlag ist eine echte Aufgabe** (Nils, 01.10.2026: *„kann ich auch nicht
   * draufklicken"*). Hier stand `art === 'aufgabe'` – geschrieben, als es die Blockart
   * `vorschlag` noch nicht gab. Die Folge war die schlechteste Mischung: Ein Vorschlag sah aus
   * wie eine Aufgabe, trug ihren Titel, ließ sich aber nicht öffnen – und wirkte deshalb wie
   * ein Platzhalter, den jemand zum Ausprobieren eingebaut hat.
   */
  if (block.aufgabe) {
    return (
      <div
        ref={setNodeRef}
        {...(ziehbar ? { ...attributes, ...listeners } : {})}
        className={cn(
          'group absolute',
          ziehbar && 'touch-none',
          isDragging && 'z-30 opacity-80 shadow-lg',
        )}
        style={lage}
        title={`${block.titel} · ${zeit}${ziehbar ? ' – ziehen, um die Uhrzeit festzulegen' : ''}`}
        onClickCapture={(e) => {
          if (Date.now() - zugEndeteUm < 400) {
            e.preventDefault()
            e.stopPropagation()
          }
        }}
      >
        <AufgabenVerweis aufgabeId={block.aufgabe.id} className={cn(klassen, 'text-left')}>
          {inhalt}
        </AufgabenVerweis>
        {uhrLoesen && (
          <button
            type="button"
            aria-label={`Uhrzeit von ${block.titel} lösen`}
            title="Uhrzeit lösen – der Plan legt die Aufgabe wieder selbst"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={uhrLoesen}
            className="absolute top-0.5 right-0.5 hidden cursor-pointer rounded bg-surface/80 p-0.5 text-sonne group-hover:block group-focus-within:block hover:text-bad"
          >
            <X className="size-3" aria-hidden />
          </button>
        )}
      </div>
    )
  }
  return (
    <div className="absolute" style={lage} title={`${block.titel} · ${zeit}`}>
      <div className={klassen}>{inhalt}</div>
    </div>
  )
}

/**
 * **Die Dauer-Überschreibungen der Arbeitswelten als Tafel**
 * ([E-390](../../../../../docs/ENTSCHEIDUNGEN.md#e-390)).
 *
 * `null` heißt „das Hausmaß gilt" – nicht „null Minuten". Deshalb werden leere Werte hier
 * **weggelassen** statt als `0` durchgereicht: `dauerMinutenVon` prüft auf „positiv", und eine
 * durchgereichte Null fiele zwar auch auf das Hausmaß zurück, aber nur zufällig. Eine Tafel, die
 * nur das enthält, was wirklich gesetzt ist, sagt die Wahrheit über sich selbst.
 */
